import { ScrollView, View } from 'react-native';

import { getEarlyIsDesktopWebBreakpoint } from 'hooks/use-media-query';
import { isWeb } from 'utils/environment';
import noop from 'utils/noop';

interface ScrollToTargetOptions {
  animated?: boolean;
  offset?: number;
}

/**
 * Scrolls a vertical `ScrollView` so that `target` is brought into view.
 *
 * Uses platform-specific APIs because `measureLayout` is not supported on react-native-web:
 * - **Mobile web**: scrolls the `ScrollView` container so the target sits `offset` px below the top edge.
 * - **Desktop web**: calls `target.scrollIntoView` and ignores `offset`.
 * - **iOS / Android**: measures `target` relative to `scrollView`, then calls `scrollView.scrollTo`.
 *
 * @param scrollView - The scroll container ref (must support `scrollTo` on native).
 * @param target - The view to scroll to (must be mounted and laid out).
 * @param options - Optional scroll behavior; defaults to `{ animated: true, offset: 0 }`.
 */
export function scrollToTargetInScrollView(
  scrollView: ScrollView,
  target: View,
  { animated = true, offset = 0 }: ScrollToTargetOptions = {}
) {
  if (isWeb) {
    const targetElement = (target as unknown) as HTMLElement;

    if (!targetElement) {
      return;
    }

    if (getEarlyIsDesktopWebBreakpoint()) {
      targetElement.scrollIntoView?.({
        behavior: animated ? 'smooth' : 'auto',
        block: 'start',
      });

      return;
    }

    const scrollContainer = scrollView.getScrollableNode?.() as HTMLElement | undefined;

    if (scrollContainer) {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      const targetTop = targetElement.getBoundingClientRect().top;
      const nextScrollTop = scrollContainer.scrollTop + (targetTop - containerTop) - offset;

      scrollContainer.scrollTo({
        top: Math.max(0, nextScrollTop),
        behavior: animated ? 'smooth' : 'auto',
      });

      return;
    }

    targetElement.scrollIntoView?.({
      behavior: animated ? 'smooth' : 'auto',
      block: 'start',
    });

    return;
  }

  target.measureLayout(
    (scrollView as unknown) as number,
    (_, y) => {
      scrollView.scrollTo({ y: Math.max(0, y - offset), animated });
    },
    noop
  );
}
